"use client";

import Link from "next/link";
import { useParams, useSearchParams } from "next/navigation";

import { useWeglot } from "@stadium/services/weglot/provider";

import { getSelectedLanguage, isForceTranslationsEnabled } from "@stadium/services/weglot/helpers";
import { DEFAULT_LANG_OBJ } from "@stadium/services/weglot/constants";
import { useSelector } from "@stadium/services/plasmic";

import { extractSlugsFromParams, generateQueryForRoutePath } from "@stadium/helpers/store";
import { DEFAULT_COUNTRY } from "@stadium/helpers/country/constants";

import styles from "./main.module.scss";
import {
  getBrandUrl,
  getCatalogEntriesPageUrl,
  getCatalogNavLinkUrl,
  getCategoryUrl,
  getCollectionUrl,
  getHeaderNavLinkUrl,
  getProductUrl,
  getStoreHomePageUrl,
} from "@stadium/shared/src/helpers/store/links";

import {
  LinkWithLangType,
  DATA_PROVIDER_STORE_MENU,
  BRAND_LINK_TYPE,
  CATEGORY_PRODUCT_LISTING_LINK_TYPE,
  STORE_HOME_PAGE_LINK_TYPE,
  PRODUCT_LINK_TYPE,
  OTHER_LINK_TYPE,
  EXTERNAL_LINK_TYPE,
  HEADER_LOGO_LINK_TYPE,
  CATALOG_HOME_LINK_TYPE,
  CATEGORY_HOME_LINK_TYPE,
} from "@stadium/shared/src/constants/stores/routes";

function getLinkUrls(
  type: LinkWithLangType,
  data: any,
  queryForRoutePath: any,
  rest: Record<string, any>,
  other: {
    baseOrigin?: string;
    isNewRouteEnabled: boolean;
  },
): { linkUrl: string } {
  let linkUrl = "";
  const { baseOrigin, isNewRouteEnabled } = other;

  switch (type) {
    case BRAND_LINK_TYPE:
      linkUrl = getBrandUrl({
        brandKey: data.key,
        queryForRoutePath,
        baseOrigin,
        isNewRouteEnabled,
      });
      break;
    case CATEGORY_HOME_LINK_TYPE:
      linkUrl = getCatalogEntriesPageUrl({
        categorySlug: data.code,
        queryForRoutePath,
        baseOrigin,
        isNewRouteEnabled,
      });
      break;
    case CATALOG_HOME_LINK_TYPE:
      linkUrl = getCatalogNavLinkUrl({
        queryForRoutePath,
        baseOrigin,
        isNewRouteEnabled,
      });
      break;
    case CATEGORY_PRODUCT_LISTING_LINK_TYPE:
      if (data?.menuIdentifier) {
        linkUrl = getCollectionUrl({
          menuIdentifier: data.menuIdentifier,
          queryForRoutePath,
          baseOrigin,
          isNewRouteEnabled,
        });
      } else {
        linkUrl = getCategoryUrl({
          category: data.code,
          queryForRoutePath,
          query: rest.query,
          baseOrigin,
          isNewRouteEnabled,
        });
      }
      break;
    case PRODUCT_LINK_TYPE:
      linkUrl = getProductUrl({
        productSlug: data.slug,
        queryForRoutePath,
        baseOrigin,
        isNewRouteEnabled,
      });
      break;
    case HEADER_LOGO_LINK_TYPE:
      linkUrl = getHeaderNavLinkUrl(queryForRoutePath, baseOrigin);
      break;
    case OTHER_LINK_TYPE:
      linkUrl = `${getHeaderNavLinkUrl(queryForRoutePath, baseOrigin)}${
        data.url || data.href || ""
      }`;
      break;
    case EXTERNAL_LINK_TYPE:
      linkUrl = data.url || data.href || data.otherUrl || "";
      break;
    case STORE_HOME_PAGE_LINK_TYPE:
      linkUrl = getStoreHomePageUrl({
        queryForRoutePath,
        baseOrigin,
        isNewRouteEnabled,
      });
      break;
    default:
      break;
  }

  return { linkUrl };
}

/** Shape from DATA_PROVIDER_STORE_MENU; link generation only reads `dynamicSlicesData.store`. */
export interface LinkWithLangDynamicSlicesData {
  store?: {
    number?: string | number;
    code?: string;
  };
}

/** Optional route defaults — when omitted, values come from `useSelector(DATA_PROVIDER_STORE_MENU)`. */
export interface LinkRouteDefaults {
  dynamicSlicesData?: LinkWithLangDynamicSlicesData;
}

export interface LinkWithLangProps extends LinkRouteDefaults {
  type: LinkWithLangType;
  data?: any;
  target?: string;
  children: React.ReactNode;
  className?: string;
  baseOrigin?: string;
  [key: string]: any;
}

const LinkWithLang = ({
  type,
  data,
  target = "_self",
  className,
  children,
  activeClassName,
  showExternalLinkIcon = false,
  baseOrigin,
  dynamicSlicesData: dynamicSlicesDataOverride,
  ...rest
}: LinkWithLangProps) => {
  const searchParams = useSearchParams();
  const forceTranslations = isForceTranslationsEnabled(searchParams);

  const selectorMenu = useSelector(DATA_PROVIDER_STORE_MENU) ?? {};
  const params = useParams();

  const dynamicSlicesData = dynamicSlicesDataOverride ?? selectorMenu.dynamicSlicesData ?? {};

  const { store } = dynamicSlicesData;

  const storeNumber = store?.number;
  const storeCode = store?.code;
  const isNewRouteEnabled = store?.storeDetail?.newRoutesEnabled;

  const extractedParams = extractSlugsFromParams(params);
  const {
    countryCode: countryCodeParams,
    lang: langParams,
    slug: storeCodeParams,
    workspaceSlug: workspaceSlugParams,
  } = extractedParams;

  const evaluatedWorkspaceSlugParams = isNewRouteEnabled
    ? workspaceSlugParams
    : (storeCodeParams as string)?.split("--")[1];

  const countryCode = countryCodeParams ?? selectorMenu?.countryCode ?? DEFAULT_COUNTRY.iso;
  const lang = langParams ?? selectorMenu?.lang ?? DEFAULT_LANG_OBJ.key;
  const workspaceSlug = evaluatedWorkspaceSlugParams ?? selectorMenu?.workspaceSlug;

  const { availableLanguages, instance } = useWeglot();

  const selectedLanguage = getSelectedLanguage({
    weglotLanguages: availableLanguages,
    langInUrl: forceTranslations ? lang : "",
    weglotInstance: instance,
    forceTranslations,
  });

  const queryForRoutePath = generateQueryForRoutePath({
    countryCode: countryCode.toLowerCase(),
    lang: selectedLanguage || lang,
    workspaceSlug,
    isNewRoute: isNewRouteEnabled,
    storeNumber,
    slug: storeCode,
  });

  const { linkUrl } = getLinkUrls(type, data, queryForRoutePath, rest, {
    baseOrigin,
    isNewRouteEnabled,
  });

  if (!linkUrl) {
    return (
      <div className={className} {...rest}>
        {children}
      </div>
    );
  }

  return (
    <Link
      href={linkUrl}
      target={target}
      prefetch={false}
      className={`${className} ${activeClassName ? activeClassName(linkUrl) : ""}`}
      {...rest}
    >
      {children}
      {target === "_blank" && showExternalLinkIcon && <span className={styles.externalLinkIcon} />}
    </Link>
  );
};

export default LinkWithLang;
